"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import MuxVideo from "@mux/mux-video-react";
import Image from "next/image";
import HeroTitleGlitch from "./HeroTitleGlitch";
type HeroData = {
  companyName?: string | null;
  leftTitle?: string | null;
  centerTitle?: string | null;
  rightTitle?: string | null;
};
type HeroContainerProps = {
  hero: HeroData | null;
};
const videoVariants = {
  initial: {
    y: "-100%"
  },
  visible: {
    y: "0%",
    // Wolny zjazd (2.5s) daje mnóstwo czasu na zbuforowanie wideo w tle
    transition: {
      duration: 2.5,
      ease: [0.65, 0.05, 0.36, 1]
    }
  }
};
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}
const PLAYBACK_ID = "cYA3DkwPfDGUf41Y7VnnFz34LFY75gIR02TMy02TSonpk";
export default function HeroContainer(t0) {
  const $ = _c(28);
  const {
    hero
  } = t0;
  const videoControls = useAnimation();
  const menuControls = useAnimation();
  const videoRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showPosterOverlay, setShowPosterOverlay] = useState(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setReducedMotion(prefersReducedMotion());
      const mq = window.matchMedia("(max-width: 767px)");
      const update = () => setIsMobile(mq.matches);
      update();
      mq.addEventListener?.("change", update);
      return () => mq.removeEventListener?.("change", update);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const width = isMobile ? "800" : "1920";
  const quality = isMobile ? "55" : "72";
  const posterUrl = `https://image.mux.com/${PLAYBACK_ID}/thumbnail.webp?time=0&width=${width}&quality=${quality}&fit_mode=preserve`;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    const params = new URLSearchParams({
      min_resolution: "720p",
      rendition_order: "desc"
    });
    t3 = PLAYBACK_ID;
    t4 = params.toString();
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  const streamUrl = `https://stream.mux.com/${t3}.m3u8?${t4}`;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => {
      if (e.target.currentTime > 0.15) {
        setShowPosterOverlay(false);
      }
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleTimeUpdate = t5;
  let t6;
  if ($[5] !== reducedMotion) {
    t6 = () => {
      if (reducedMotion) {
        return;
      }
      const el = videoRef.current;
      if (!el?.play) {
        return;
      }
      const tryPlay = () => {
        if (el.paused) {
          el.play().catch(_temp);
        }
      };
      tryPlay();
      requestAnimationFrame(tryPlay);
      setTimeout(tryPlay, 200);
      setTimeout(tryPlay, 500);
    };
    $[5] = reducedMotion;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const primeAndPlay = t6;
  const t7 = hero as any;
  let t8;
  if ($[7] !== menuControls || $[8] !== t7 || $[9] !== videoControls) {
    t8 = <div className="relative z-20 h-full w-full px-[2%]"><div className="mx-auto flex h-full w-full items-center justify-center"><HeroTitleGlitch hero={t7} videoControls={videoControls} menuControls={menuControls} /></div></div>;
    $[7] = menuControls;
    $[8] = t7;
    $[9] = videoControls;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      willChange: "transform"
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== posterUrl || $[13] !== reducedMotion || $[14] !== showPosterOverlay) {
    t10 = !reducedMotion && <div className="absolute inset-0 z-20 pointer-events-none transition-opacity duration-700 ease-out" style={{
      opacity: showPosterOverlay ? 1 : 0
    }}><Image src={posterUrl} alt="PassWorld Travels&Events" fill={true} priority={true} fetchPriority="high" unoptimized={true} className="object-cover" /></div>;
    $[12] = posterUrl;
    $[13] = reducedMotion;
    $[14] = showPosterOverlay;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== primeAndPlay || $[17] !== reducedMotion) {
    t11 = !reducedMotion && <MuxVideo ref={videoRef} src={streamUrl} className="absolute inset-0 h-full w-full object-cover" autoPlay={true} muted={true} loop={true} playsInline={true} preload="auto" onTimeUpdate={handleTimeUpdate} onCanPlay={primeAndPlay} onError={() => setShowPosterOverlay(true)} style={{
      transform: "translateZ(0)",
      backfaceVisibility: "hidden"
    }} />;
    $[16] = primeAndPlay;
    $[17] = reducedMotion;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="absolute inset-0 bg-black/30 pointer-events-none z-30" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== primeAndPlay || $[21] !== t10 || $[22] !== t11 || $[23] !== videoControls) {
    t13 = <motion.div className="absolute inset-0 z-10" initial="initial" animate={videoControls} variants={videoVariants} style={t9} onAnimationStart={primeAndPlay}>{t10}{t11}{t12}</motion.div>;
    $[20] = primeAndPlay;
    $[21] = t10;
    $[22] = t11;
    $[23] = videoControls;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t8) {
    t14 = <section id="hero" className="relative h-[100dvh] w-full overflow-hidden bg-black">{t8}{t13}</section>;
    $[25] = t13;
    $[26] = t8;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}
function _temp() {}